import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "../api/api";
import { ArrowLeft, Clock, Ticket, CheckSquare, Users, Star, MessageSquare, BarChart2 } from "lucide-react";

const fmtMin = (m: number) => { const h = Math.floor(m/60); return `${h}:${String(m%60).padStart(2,'0')}`; };


const ALL_TYPES = ["Ticket","Task","SubTask","WorkLog","CRM","Quality","Communication","Project"];

const MONTHS = ["January","February","March","April","May","June","July","August","September","October","November","December"];

function badge(status: string) {
  const map: any = {
    OPEN:"bg-blue-100 text-blue-700", IN_PROGRESS:"bg-yellow-100 text-yellow-700",
    CLOSED:"bg-green-100 text-green-700", COMPLETED:"bg-green-100 text-green-700",
    PENDING:"bg-orange-100 text-orange-700", ACTIVE:"bg-blue-100 text-blue-700",
    ON_HOLD:"bg-gray-100 text-gray-600", CANCELLED:"bg-red-100 text-red-700",
  };
  return <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${map[status]||"bg-gray-100 text-gray-600"}`}>{status?.replace("_"," ")}</span>;
}

export default function UserActivity() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  // filters
  const now = new Date();
  const [typeFilter, setTypeFilter]   = useState("All");
  const [fromDate, setFromDate]       = useState("");
  const [toDate, setToDate]           = useState("");
  const [deptFilter, setDeptFilter]   = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [monthFilter, setMonthFilter] = useState("");
  const [yearFilter, setYearFilter]   = useState(String(now.getFullYear()));

  const load = () => {
    setLoading(true);
    const params: any = {};
    if (fromDate) params.from = fromDate;
    if (toDate)   params.to   = toDate;
    api.get(`/users/${id}/activity`, { params })
      .then(r => { setData(r.data); setLoading(false); })
      .catch(() => setLoading(false));
  };

  useEffect(() => { load(); }, [id]);

  // Build unified activity rows
  const buildRows = () => {
    if (!data) return [];
    const rows: any[] = [];

    data.tickets?.forEach((t: any) => rows.push({
      type:"Ticket", ref: t.ticketNumber || t.title, dept: t.department||"-",
      status: t.status, hours: t.timeSpent||0, date: t.createdAt, id: t.id,
    }));
    data.tasks?.forEach((t: any) => rows.push({
      type:"Task", ref: t.title, dept: t.department||"-",
      status: t.status, hours: t.totalLoggedMinutes||0, date: t.createdAt, id: t.id,
    }));
    data.subTasks?.forEach((t: any) => rows.push({
      type:"SubTask", ref: t.title, dept: t.project?.name||"-",
      status: t.status, hours: t.totalLoggedMinutes||0, date: t.createdAt, id: t.id,
    }));
    data.workLogs?.forEach((l: any) => rows.push({
      type:"WorkLog", ref: l.task?.title || l.subTask?.title || l.project?.name || l.ticket?.title || "Direct Log",
      dept: "-", status: "-", hours: l.minutes, date: l.date, id: l.id,
    }));
    data.crmEntries?.forEach((c: any) => rows.push({
      type:"CRM", ref: c.customerName+(c.company?` (${c.company})`:""), dept: "-",
      status: c.status, hours: 0, date: c.createdAt, id: c.id,
    }));
    data.quality?.forEach((q: any) => rows.push({
      type:"Quality", ref: q.referenceNumber||q.interactionType, dept: "-",
      status: `Score: ${q.score}`, hours: 0, date: q.createdAt, id: q.id,
    }));
    data.communications?.forEach((c: any) => rows.push({
      type:"Communication", ref: c.customerName||c.summary, dept: "-",
      status: c.type+(c.customType?` (${c.customType})`:""), hours: 0, date: c.createdAt, id: c.id,
    }));
    data.projects?.forEach((p: any) => rows.push({
      type:"Project", ref: p.name, dept: p.department||"-",
      status: p.status, hours: 0, date: p.createdAt, id: p.id,
    }));

    return rows;
  };

  const applyFilters = (rows: any[]) => {
    return rows.filter(r => {
      if (typeFilter !== "All" && r.type !== typeFilter) return false;
      if (deptFilter && r.dept.toLowerCase().indexOf(deptFilter.toLowerCase()) === -1) return false;
      if (statusFilter && r.status.toLowerCase().indexOf(statusFilter.toLowerCase()) === -1) return false;
      if (monthFilter) {
        const d = new Date(r.date);
        const mIdx = MONTHS.indexOf(monthFilter);
        if (d.getMonth() !== mIdx) return false;
        if (yearFilter && d.getFullYear() !== parseInt(yearFilter)) return false;
      } else {
        if (fromDate && new Date(r.date) < new Date(fromDate)) return false;
        if (toDate   && new Date(r.date) > new Date(toDate+"T23:59:59")) return false;
      }
      return true;
    });
  };

  const filteredRows = applyFilters(buildRows()).sort((a,b) => new Date(b.date).getTime() - new Date(a.date).getTime());

  // Monthly hours filtered by year + month selection
  const filteredMonthlyHours = () => {
    if (!data) return [];
    return data.monthlyHours?.filter((m: any) => {
      if (yearFilter && !m.month.startsWith(yearFilter)) return false;
      return true;
    });
  };

  const totalHoursThisMonth = () => {
    if (!monthFilter || !data) return null;
    const mIdx = String(MONTHS.indexOf(monthFilter)+1).padStart(2,"0");
    const key = `${yearFilter}-${mIdx}`;
    const found = data.monthlyHours?.find((m: any) => m.month === key);
    return found?.hours ?? 0;
  };

  const thisMonthHours = totalHoursThisMonth();
  const years = Array.from({length:5},(_,i)=>String(now.getFullYear()-i));

  if (loading) return <div className="p-8 text-center text-gray-500">Loading...</div>;
  if (!data?.user) return <div className="p-8 text-center text-gray-500">User not found.</div>;

  const { user, summary, monthlyHours } = data;

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <button onClick={() => navigate("/users")} className="p-2 rounded-lg hover:bg-gray-100">
          <ArrowLeft size={20} />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">User Activity Dashboard</h1>
          <p className="text-gray-500 text-sm">{user.name} · {user.email} · <span className="capitalize">{user.role?.toLowerCase()}</span>{user.department ? ` · ${user.department}` : ""}</p>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
        {[
          { icon: <Ticket size={18}/>, label:"Tickets", value: summary.totalTickets, color:"text-blue-600 bg-blue-50" },
          { icon: <CheckSquare size={18}/>, label:"Tasks Done", value: summary.totalTasks, color:"text-green-600 bg-green-50" },
          { icon: <Clock size={18}/>, label:"Total Hours", value: `${summary.totalHours}h`, color:"text-purple-600 bg-purple-50" },
          { icon: <Star size={18}/>, label:"Avg Quality", value: summary.avgQuality||"-", color:"text-yellow-600 bg-yellow-50" },
          { icon: <Users size={18}/>, label:"CRM", value: summary.totalCRM, color:"text-pink-600 bg-pink-50" },
          { icon: <MessageSquare size={18}/>, label:"Comms", value: summary.totalCommunications, color:"text-indigo-600 bg-indigo-50" },
          { icon: <BarChart2 size={18}/>, label:"Quality", value: summary.totalQuality, color:"text-orange-600 bg-orange-50" },
        ].map(c => (
          <div key={c.label} className={`rounded-xl p-3 flex items-center gap-2 ${c.color.split(" ")[1]}`}>
            <span className={c.color.split(" ")[0]}>{c.icon}</span>
            <div>
              <p className="text-xs text-gray-500">{c.label}</p>
              <p className="font-bold text-gray-800">{c.value}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Monthly Hours Summary */}
      <div className="bg-white rounded-xl border border-gray-200 p-4">
        <h2 className="font-semibold text-gray-800 mb-3 flex items-center gap-2"><Clock size={16}/>Monthly Hours Summary</h2>
        <div className="flex flex-wrap gap-2">
          {filteredMonthlyHours()?.length === 0 && <p className="text-gray-400 text-sm">No hours logged yet.</p>}
          {filteredMonthlyHours()?.map((m: any) => {
            const [y, mo] = m.month.split("-");
            const label = `${MONTHS[parseInt(mo)-1]} ${y}`;
            return (
              <div key={m.month} className="bg-purple-50 border border-purple-100 rounded-lg px-3 py-2 text-sm">
                <span className="text-gray-600">{label}</span>
                <span className="ml-2 font-bold text-purple-700">{m.hours}h</span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-gray-200 p-4">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          {/* Activity Type */}
          <div>
            <label className="block text-xs text-gray-500 mb-1">Activity Type</label>
            <select value={typeFilter} onChange={e=>setTypeFilter(e.target.value)}
              className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-sm">
              <option value="All">All Types</option>
              {ALL_TYPES.map(t=><option key={t}>{t}</option>)}
            </select>
          </div>
          {/* Month */}
          <div>
            <label className="block text-xs text-gray-500 mb-1">Month</label>
            <select value={monthFilter} onChange={e=>setMonthFilter(e.target.value)}
              className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-sm">
              <option value="">All Months</option>
              {MONTHS.map(m=><option key={m}>{m}</option>)}
            </select>
          </div>
          {/* Year */}
          <div>
            <label className="block text-xs text-gray-500 mb-1">Year</label>
            <select value={yearFilter} onChange={e=>setYearFilter(e.target.value)}
              className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-sm">
              {years.map(y=><option key={y}>{y}</option>)}
            </select>
          </div>
          {/* From */}
          <div>
            <label className="block text-xs text-gray-500 mb-1">From Date</label>
            <input type="date" value={fromDate} onChange={e=>{setFromDate(e.target.value);setMonthFilter("");}}
              className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-sm"/>
          </div>
          {/* To */}
          <div>
            <label className="block text-xs text-gray-500 mb-1">To Date</label>
            <input type="date" value={toDate} onChange={e=>{setToDate(e.target.value);setMonthFilter("");}}
              className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-sm"/>
          </div>
          {/* Status */}
          <div>
            <label className="block text-xs text-gray-500 mb-1">Status / Keyword</label>
            <input type="text" value={statusFilter} onChange={e=>setStatusFilter(e.target.value)}
              placeholder="e.g. Closed"
              className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-sm"/>
          </div>
        </div>
        <div className="flex items-center gap-2 mt-3">
          <button onClick={load} className="bg-blue-600 text-white px-4 py-1.5 rounded-lg text-sm hover:bg-blue-700">Apply Date Range</button>
          <button onClick={()=>{setFromDate("");setToDate("");setMonthFilter("");setStatusFilter("");setTypeFilter("All");setDeptFilter("");setYearFilter(String(now.getFullYear()));}}
            className="border border-gray-200 text-gray-600 px-4 py-1.5 rounded-lg text-sm hover:bg-gray-50">Reset</button>
          {thisMonthHours !== null && (
            <span className="ml-auto text-sm font-semibold text-purple-700 bg-purple-50 px-3 py-1.5 rounded-lg">
              Total Hours in {monthFilter} {yearFilter}: {thisMonthHours}h
            </span>
          )}
        </div>
      </div>

      {/* Activity Table */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
          <h2 className="font-semibold text-gray-800">Activity Log</h2>
          <span className="text-xs text-gray-400">{filteredRows.length} records</span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-100">
              <tr>
                {["Type","Reference","Department","Status","Hours","Date"].map(h=>(
                  <th key={h} className="px-4 py-2 text-left text-xs font-medium text-gray-500">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {filteredRows.length === 0 && (
                <tr><td colSpan={6} className="text-center py-10 text-gray-400">No activity found for selected filters.</td></tr>
              )}
              {filteredRows.map((r,i) => (
                <tr key={i} className="hover:bg-gray-50">
                  <td className="px-4 py-2.5">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                      r.type==="Ticket"?"bg-blue-100 text-blue-700":
                      r.type==="Task"?"bg-green-100 text-green-700":
                      r.type==="SubTask"?"bg-teal-100 text-teal-700":
                      r.type==="WorkLog"?"bg-purple-100 text-purple-700":
                      r.type==="CRM"?"bg-pink-100 text-pink-700":
                      r.type==="Quality"?"bg-orange-100 text-orange-700":
                      r.type==="Communication"?"bg-indigo-100 text-indigo-700":
                      "bg-gray-100 text-gray-700"
                    }`}>{r.type}</span>
                  </td>
                  <td className="px-4 py-2.5 font-medium text-gray-800 max-w-[200px] truncate">{r.ref}</td>
                  <td className="px-4 py-2.5 text-gray-500">{r.dept}</td>
                  <td className="px-4 py-2.5">{r.status !== "-" ? badge(r.status) : <span className="text-gray-400">-</span>}</td>
                  <td className="px-4 py-2.5 text-gray-600">{r.hours > 0 ? `${r.hours}h` : "-"}</td>
                  <td className="px-4 py-2.5 text-gray-500 whitespace-nowrap">{new Date(r.date).toLocaleDateString("en-GB",{day:"2-digit",month:"short",year:"numeric"})}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
